import { useState } from "react";
import { useStore } from "../lib/store";
import { drainOutbox, listOutbox, removeOutbox } from "../lib/outbox";

/**
 * Toont een gele balk bovenaan de app zodra er berichten in de offline-
 * outbox staan. Klik op de balk klapt 'm uit met een lijst van de
 * wachtende berichten — handig om te zien wat je probeerde te sturen
 * (en om eventueel ééntje te verwijderen die je toch niet meer wil).
 * Drainer loopt automatisch bij socket-reconnect en window.online;
 * "Probeer nu" is voor handmatige retry zonder volledige reconnect.
 */
export default function OutboxBanner() {
  const items = useStore((s) => s.outboxItems);
  const setOutboxItems = useStore((s) => s.setOutboxItems);
  const conversations = useStore((s) => s.conversations);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  async function retry() {
    setBusy(true);
    try {
      await drainOutbox(async () => {
        setOutboxItems(await listOutbox());
      });
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    await removeOutbox(id);
    setOutboxItems(await listOutbox());
  }

  function convLabel(conversationId: number): string {
    const c = conversations[conversationId];
    if (!c) return `Gesprek #${conversationId}`;
    if (c.kind === "channel") return `#${c.name ?? "kanaal"}`;
    const otherId = c.member_ids.find((id) => id !== me?.id);
    return otherId !== undefined ? users[otherId]?.display_name ?? `Gesprek #${conversationId}` : "Gesprek";
  }

  return (
    <div className="border-b border-amber-700 bg-amber-950/60 text-xs text-amber-200">
      <div className="flex items-center gap-2 px-3 py-1">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
        </svg>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex-1 truncate text-left hover:underline"
          title={expanded ? "Lijst inklappen" : "Bekijk wachtende berichten"}
        >
          {items.length} bericht{items.length === 1 ? "" : "en"} wacht
          {items.length === 1 ? "" : "en"} op verbinding {expanded ? "▴" : "▾"}
        </button>
        <button
          onClick={() => void retry()}
          disabled={busy}
          className="rounded bg-amber-800/60 px-2 py-0.5 font-medium hover:bg-amber-800 disabled:opacity-50"
        >
          {busy ? "Bezig…" : "Probeer nu"}
        </button>
      </div>
      {expanded && (
        <ul className="max-h-48 overflow-y-auto border-t border-amber-800/60">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-start gap-2 border-b border-amber-900/40 px-3 py-1 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-amber-300">
                  → {convLabel(it.conversation_id)}
                  {it.attempt_count > 0 && (
                    <span className="ml-2 text-amber-500">
                      ({it.attempt_count} keer geprobeerd)
                    </span>
                  )}
                </div>
                <div className="truncate text-amber-100/80">{it.body}</div>
              </div>
              <button
                onClick={() => void discard(it.id)}
                className="shrink-0 rounded px-1 text-amber-400 hover:bg-amber-800 hover:text-white"
                title="Bericht verwijderen uit wachtrij"
                aria-label="Verwijderen"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
